import { createFileRoute } from "@tanstack/react-router";
import { Calendar, Clock, Video } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/bookings-hero.webp";
import reserveImg from "@/assets/bookings-reserve.webp";
import paymentsImg from "@/assets/bookings-payments.webp";

const IMG: Record<string, string> = { hero: heroImg, reserve: reserveImg, payments: paymentsImg };

export const Route = createFileRoute("/vi/platform/bookings")({
  head: () => ({
    meta: [
      { title: "Đặt lịch & Cuộc họp — banana.cards" },
      { name: "description", content: "Khách hàng đặt lịch trực tiếp từ thẻ của bạn. Đồng bộ lịch, thanh toán và nhắc nhở — tích hợp sẵn." },
      { property: "og:title", content: "Đặt lịch & Cuộc họp — banana.cards" },
      { property: "og:description", content: "Khách hàng đặt lịch trực tiếp từ thẻ của bạn." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/bookings", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Đặt lịch & Cuộc họp"
      title="Khách hàng đặt lịch"
      italic="ngay từ thẻ của bạn."
      description="Nhúng lịch trống trực tiếp lên thẻ. Khách chọn slot, đồng bộ vào lịch, và liên hệ được tạo — tự động."
      heroImage={IMG['hero']}
      metrics={[{ v: "3x", l: "nhiều cuộc họp hơn" }, { v: "2 chiều", l: "đồng bộ lịch" }, { v: "0", l: "đặt trùng" }]}
      bullets={[
        { icon: Calendar, title: "Đặt ở mọi nơi", desc: "Trực tiếp từ thẻ, link, QR, chữ ký hoặc Wallet pass." },
        { icon: Clock, title: "Lịch trống thông minh", desc: "Buffer, giờ làm việc, múi giờ và thời gian chuẩn bị — được xử lý." },
        { icon: Video, title: "Link video tự động", desc: "Zoom, Google Meet hoặc Microsoft Teams tạo tự động." }
      ]}
      deepDives={[
        { eyebrow: "Lịch", title: "Một nguồn sự thật.", italic: "Luôn luôn.", description: "Đồng bộ 2 chiều với Google, Outlook và Apple Calendar — thấy lịch trống thực và không bao giờ đặt trùng.", image: IMG["reserve"], reverse: false, points: ["Google, Outlook & Apple Calendar", "Loại sự kiện và thời lượng riêng", "Round-robin cho đội", "Buffer và lead time tùy chỉnh"] },
        { eyebrow: "Thanh toán", title: "Nhận đặt cọc", italic: "khi đặt lịch.", description: "Tính phí đặt cọc hoặc toàn bộ khi slot được đặt — hoàn hảo cho consult, dịch vụ và call high-intent.", image: IMG["payments"], reverse: true, points: ["Tích hợp Stripe & Paddle", "Đặt cọc hoặc thanh toán đầy đủ", "Giá theo loại sự kiện", "Quy tắc hoàn tiền và đổi lịch"] }
      ]}
      howItWorks={[
        { title: "Đặt lịch trống", desc: "Giờ làm việc, buffer và múi giờ." },
        { title: "Thêm vào thẻ", desc: "Nút đặt lịch một click khắp nơi." },
        { title: "Khách đặt slot", desc: "Lịch đồng bộ thời gian thực." },
        { title: "Họp & follow-up", desc: "AI tóm tắt sau cuộc gọi." }
      ]}
      capabilities={["Đồng bộ nhiều lịch", "Round-robin & lập lịch tập thể", "Sự kiện nhóm và waitlist", "Nhận thanh toán khi đặt", "Form tùy chỉnh theo loại sự kiện", "Nhắc nhở SMS & email", "Widget nhúng cho mọi site", "Action Zapier và webhook"]}
      faq={[
        { q: "Tôi có thể cho khách đặt loại dịch vụ nào?", a: "Ba loại: Cuộc hẹn (1:1 hoặc nhóm nhỏ — consult, demo, viewing), Lớp học (buổi định kỳ — yoga, fitness, ngôn ngữ), và Khóa học (chương trình nhiều buổi — bootcamp, cohort). Mỗi loại có thời lượng, sức chứa, địa điểm, form và giá riêng." },
        { q: "Banana đồng bộ với lịch nào?", a: "Đồng bộ 2 chiều với Google Calendar, Microsoft Outlook và Apple iCloud — đọc busy time và viết booking đã xác nhận để không bao giờ đặt trùng." },
        { q: "Có thể nhận thanh toán khi đặt không?", a: "Có. Kết nối Stripe để tính phí đặt cọc, toàn bộ hoặc gói khi đặt. Lớp và khóa học có nhiều tier giá — miễn phí, theo buổi, hoặc gói nhiều buổi." },
        { q: "Hoạt động cho đội và round-robin thế nào?", a: "Gán dịch vụ cho một, một nhóm, hoặc round-robin toàn đội. Banana kiểm tra lịch của mỗi người và đề xuất hợp của slot trống, rồi route booking đến người được gán." }
      ]}
      outro={{ title: "Lịch đầy.", italic: "Hộp thư rỗng.", description: "Đặt lịch phù hợp với ngày và túi tiền của bạn." }}
    />
  );
}